import { Zap, Battery, Truck, Wifi, WifiOff, Activity, List, Unplug, Leaf } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ReportSummary } from '@/types/reports';
import { cn } from '@/lib/utils';
import { MODEL_LABELS } from '@/lib/constants';
import { MAX_FOCUS_UNIT_IDS, type UnitStatusFocus } from '@/hooks/useReportFocus';

interface ReportSummaryCardsProps {
  summary: ReportSummary;
}

interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  iconColor?: string;
}

function SummaryCard({ title, value, subtitle, icon, iconColor = 'text-[#8FB560]' }: SummaryCardProps) {
  return (
    <Card className="p-4 bg-card border-border">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-muted/50 ${iconColor}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

export function ReportSummaryCards({ summary }: ReportSummaryCardsProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <SummaryCard
        title="Total Units"
        value={summary.totalUnits}
        subtitle={`${summary.onlineUnits} online`}
        icon={<Truck className="h-5 w-5" />}
      />
      <SummaryCard
        title="Energy Generated"
        value={summary.totalEnergyGenerated >= 1_000_000
          ? `${(summary.totalEnergyGenerated / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`
          : `${Math.round(summary.totalEnergyGenerated).toLocaleString()} kWh`}
        subtitle="Total solar output"
        icon={<Zap className="h-5 w-5" />}
        iconColor="text-amber-500"
      />
      <SummaryCard
        title="Off-Grid"
        value={`${summary.totalOffGridPercentage}%`}
        subtitle="Overall Average"
        icon={<Unplug className="h-5 w-5" />}
        iconColor="text-emerald-500"
      />
      <SummaryCard
        title="CO₂ Reduced"
        value={`${Math.round(summary.totalCO2Reduced).toLocaleString()} kg`}
        subtitle="Carbon offset"
        icon={<Leaf className="h-5 w-5" />}
        iconColor="text-green-500"
      />
    </div>
  );
}

interface StatusBreakdownProps {
  summary?: ReportSummary;
  /** Status the report is focused on (click-to-focus), if any */
  selectedStatus?: UnitStatusFocus | null;
  /** Makes each row clickable: focuses the report on those units (a second click clears) */
  onSelectStatus?: (status: UnitStatusFocus) => void;
  /** Clears the focus */
  onClear?: () => void;
}

const STATUS_ROWS: {
  key: UnitStatusFocus;
  label: string;
  icon: typeof Wifi;
  iconClass: string;
  barClass: string;
  count: (s: ReportSummary) => number;
}[] = [
  { key: 'online', label: 'Online', icon: Wifi, iconClass: 'text-emerald-500', barClass: 'bg-emerald-500', count: s => s.onlineUnits },
  { key: 'offline', label: 'Offline', icon: WifiOff, iconClass: 'text-red-500', barClass: 'bg-red-500', count: s => s.offlineUnits },
  { key: 'idle', label: 'Idle/Charging', icon: Battery, iconClass: 'text-amber-500', barClass: 'bg-amber-500', count: s => s.idleUnits },
];

export function StatusBreakdown({ summary, selectedStatus = null, onSelectStatus, onClear }: StatusBreakdownProps) {
  const hasData = !!summary;
  const total = summary?.totalUnits ?? 0;

  return (
    <Card className="p-4 bg-card border-border">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <Activity className={cn("h-4 w-4", hasData ? "text-[#8FB560]" : "text-muted-foreground/50")} />
          Unit Status
        </h3>
        {selectedStatus && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      <div className="space-y-3">
        {STATUS_ROWS.map(({ key, label, icon: Icon, iconClass, barClass, count }) => {
          const n = hasData ? count(summary!) : 0;
          const percent = total > 0 ? Math.round((n / total) * 100) : 0;
          const body = (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={cn("h-3.5 w-3.5", hasData ? iconClass : "text-muted-foreground/30")} />
                  <span className="text-sm text-muted-foreground">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {hasData ? (
                    <>
                      <span className="text-sm font-medium tabular-nums">{n}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">({percent}%)</span>
                    </>
                  ) : (
                    <Skeleton className="h-4 w-12" />
                  )}
                </div>
              </div>
              <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
                {hasData ? (
                  <div className={cn("h-full transition-all", barClass)} style={{ width: `${percent}%` }} />
                ) : (
                  <Skeleton className="h-full w-full" />
                )}
              </div>
            </>
          );
          if (!onSelectStatus || !hasData) {
            return <div key={key}>{body}</div>;
          }
          // Click-to-focus: the focused row stays full strength, the rest fade.
          const isSelected = selectedStatus === key;
          const tooMany = n > MAX_FOCUS_UNIT_IDS;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={isSelected}
              aria-label={`Show only ${label} units: ${n}`}
              title={tooMany ? 'Too many units to focus — narrow the filters' : undefined}
              disabled={n === 0 || tooMany}
              onClick={() => onSelectStatus(key)}
              className={cn(
                'block w-full rounded text-left transition-opacity disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB560]',
                selectedStatus && !isSelected && 'opacity-40 hover:opacity-70',
              )}
            >
              {body}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

interface ModelBreakdownProps {
  modelCounts?: Record<string, number>;
  totalUnits?: number;
  /** Set to true when data is loaded but empty (vs undefined = loading) */
  isLoaded?: boolean;
  /** Additional className for styling (e.g., flex-1 for expansion) */
  className?: string;
  /** Model the report is focused on (click-to-focus), if any */
  selectedModel?: string | null;
  /** Makes each row clickable: focuses the report on that model (a second click clears) */
  onSelectModel?: (modelCode: string) => void;
  /** Clears the focus */
  onClear?: () => void;
}

export function ModelBreakdown({
  modelCounts,
  totalUnits = 0,
  isLoaded: _isLoaded = false,
  className,
  selectedModel = null,
  onSelectModel,
  onClear,
}: ModelBreakdownProps) {
  // Distinguish between "loading" (undefined) and "empty result" (empty object)
  const hasData = modelCounts && Object.keys(modelCounts).length > 0;
  const isEmptyResult = modelCounts !== undefined && Object.keys(modelCounts).length === 0;

  // Convert to array and calculate percentages - show all entries, no limit
  const breakdown = hasData
    ? Object.entries(modelCounts)
        .map(([modelCode, count]) => ({
          code: modelCode,
          model: MODEL_LABELS[modelCode] || modelCode,
          count,
          percentage: totalUnits > 0 ? Math.round((count / totalUnits) * 100) : 0
        }))
        .sort((a, b) => b.count - a.count)
    : [];

  // Skeleton placeholder data
  const skeletonRows = [
    { width: '75%' },
    { width: '60%' },
    { width: '45%' },
    { width: '30%' },
  ];

  return (
    <Card className={cn("p-4 bg-card border-border", className)}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <List className="h-4 w-4 text-[#8FB560]" />
          Units by Model
        </h3>
        {selectedModel && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      <div className="space-y-2.5">
        {hasData ? (
          breakdown.map((item) => {
            const bar = (
              <div className="relative h-6 bg-muted rounded overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[#8FB560] transition-all duration-500"
                  style={{ width: `${Math.max(item.percentage, 8)}%` }}
                />
                {/* Labels positioned on bar - white text readable on both green and gray */}
                <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
                  <span className="font-medium text-white truncate pr-2 z-10 drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">{item.model}</span>
                  <span className="text-white/90 tabular-nums whitespace-nowrap z-10 drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">
                    {item.count} ({item.percentage}%)
                  </span>
                </div>
              </div>
            );
            if (!onSelectModel) {
              return <div key={item.code} className="space-y-1">{bar}</div>;
            }
            // Click-to-focus: the focused row stays full strength, the rest fade.
            const isSelected = selectedModel === item.code;
            return (
              <button
                key={item.code}
                type="button"
                aria-pressed={isSelected}
                aria-label={`Show only ${item.model} (${item.code}): ${item.count} units`}
                onClick={() => onSelectModel(item.code)}
                className={cn(
                  'block w-full rounded text-left transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB560]',
                  selectedModel && !isSelected && 'opacity-40 hover:opacity-70',
                )}
              >
                {bar}
              </button>
            );
          })
        ) : isEmptyResult ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            0 units match current filters
          </div>
        ) : (
          skeletonRows.map((row, idx) => (
            <div key={idx} className="relative h-6 bg-muted rounded overflow-hidden">
              <Skeleton className="absolute inset-y-0 left-0 h-full" style={{ width: row.width }} />
            </div>
          ))
        )}
      </div>
    </Card>
  );
}
